import React from 'react';
import { Check, CloudOff, Cloud, Loader2 } from 'lucide-react';
import type { AutosaveStatus } from '../hooks/useAutosave';

interface AutosaveIndicatorProps {
  status: AutosaveStatus;
  lastSavedAt: Date | null;
  lastError: string | null;
  retryCount: number;
  className?: string;
  showLastSaved?: boolean;
  canEnableAutosave?: boolean;
  onEnableAutosave?: () => void;
}

const AutosaveIndicator: React.FC<AutosaveIndicatorProps> = ({
  status,
  lastSavedAt,
  lastError,
  retryCount,
  className = '',
  showLastSaved = true,
  canEnableAutosave = true,
  onEnableAutosave
}) => {
  const isEnabled = status !== 'disabled';
  const isConnected = status === 'idle' || status === 'saving' || status === 'saved';
  
  const getStatusText = () => {
    if (!isEnabled) return 'Autosave disabled';
    if (status === 'saving') return 'Saving changes...';
    if (status === 'saved') {
      if (lastSavedAt && showLastSaved) {
        const now = new Date();
        const diffMs = now.getTime() - lastSavedAt.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        if (diffMins < 1) return 'Saved just now';
        if (diffMins === 1) return 'Saved 1 min ago';
        if (diffMins < 60) return `Saved ${diffMins} mins ago`;
        return `Saved at ${lastSavedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
      }
      return 'All changes saved';
    }
    if (status === 'error') return lastError || 'Save failed';
    if (status === 'offline') return 'Offline - will save when connected';
    return 'Autosave active';
  };

  const getStatusIcon = () => {
    if (!isEnabled) return <CloudOff className="w-4 h-4 text-slate-400" />;
    if (status === 'saving') return <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />;
    if (status === 'error') return <CloudOff className="w-4 h-4 text-red-500" />;
    if (status === 'offline') return <CloudOff className="w-4 h-4 text-amber-500" />;
    return <Check className="w-4 h-4 text-emerald-500" />;
  };

  const getButtonStyle = () => {
    if (!isEnabled) {
      return 'bg-slate-50/80 hover:bg-slate-100/80 text-slate-600 shadow-sm';
    }
    if (status === 'error') {
      return 'bg-red-50/80 hover:bg-red-100/80 text-red-600 shadow-sm';
    }
    if (status === 'offline') {
      return 'bg-amber-50/80 hover:bg-amber-100/80 text-amber-600 shadow-sm';
    }
    if (status === 'saving') {
      return 'bg-blue-50/80 text-blue-600 shadow-sm';
    }
    return 'bg-white/80 hover:bg-white text-slate-700 shadow-sm';
  };

  const handleClick = () => {
    if (!isEnabled && onEnableAutosave) {
      onEnableAutosave();
    }
  };

  const isClickable = !isEnabled && onEnableAutosave;

  return (
    <button
      onClick={isClickable ? handleClick : undefined}
      disabled={!isClickable}
      className={`
        inline-flex items-center gap-2 px-3 py-1.5 rounded-full
        backdrop-blur-sm border border-slate-200/50
        transition-all duration-300 ease-out
        ${getButtonStyle()}
        ${isClickable ? 'cursor-pointer hover:scale-105' : 'cursor-default'}
        ${className}
      `}
      title={
        !isEnabled 
          ? (canEnableAutosave ? 'Click to enable autosave' : 'Add property address to enable autosave')
          : status === 'error' 
            ? `Save error: ${lastError || 'Unknown error'}`
            : status === 'offline'
              ? 'Autosave paused - device is offline'
            : status === 'saving'
              ? 'Saving your changes...'
              : `Autosave enabled${lastSavedAt && showLastSaved ? ` - Last saved: ${lastSavedAt.toLocaleTimeString()}` : ''}`
      }
    >
      {getStatusIcon()}
      <span className="text-xs font-medium tracking-wide whitespace-nowrap">
        {getStatusText()}
      </span>
    </button>
  );
};

export default AutosaveIndicator; 